import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useRmsState } from '../context/RmsStateContext';
import { apiGetReferralParticipants } from '../utils/rmsApi';
import useOrgHfpInstrument from './useOrgHfpInstrument';
import {
  STATUS,
  buildFormProgressFromRecord,
  calculateRoleStatus,
  calculateSenRoleStatus,
  isRoleReferralFormsComplete,
  areAllTriageFormsComplete,
  isSenOnlyRecord,
  recordRequiresSubmissionConfirmation,
  allRequiredRespondentsConfirmed,
} from '../utils/referralStatus';
import { canConfirmSubmission } from '../utils/submissionConfirmation';
import { PARTICIPANT_ROLE } from '../../constants/participantStatuses';
import {
  buildReferralPathwayRows,
  buildSenLane,
  deriveYearGroup,
  referralLaneRows,
  roleFormChip,
  teacherNamesForLane,
} from '../utils/harbourPupils';

/** One pupil's people: the guardian (linked preferred), the school contact and listed teachers. */
async function fetchParticipantData(orgId, patientRecordId) {
  try {
    const { participants } = await apiGetReferralParticipants({ orgId, patientRecordId });
    const guardians = Array.isArray(participants)
      ? participants.filter((p) => p.role === PARTICIPANT_ROLE.GUARDIAN && p.status !== 'superseded')
      : [];
    // Prefer a linked guardian over a pending one (mirrors the backend).
    const guardianParticipant =
      guardians.find((p) => p.status === 'account_linked') || guardians[0] || null;
    // The school's person on the record (the SENCo), so family screens can
    // say who still has forms to finish rather than "the school".
    const schoolUsers = Array.isArray(participants)
      ? participants.filter((p) => p.role === PARTICIPANT_ROLE.SCHOOL_USER && p.status !== 'superseded')
      : [];
    const schoolContact = schoolUsers.find((p) => p.status === 'account_linked') || schoolUsers[0] || null;
    // Teachers on the record, with their form allocations (TDI-489). A
    // removed teacher keeps their history but leaves every surface.
    const teachers = Array.isArray(participants)
      ? participants.filter((p) => p.role === PARTICIPANT_ROLE.TEACHER
          && !['superseded', 'removed', 'disabled', 'archived', 'left'].includes(p.status))
      : [];
    // Teachers who no longer work at the school (TDI-617): off the record,
    // but the forms they finished still say who completed them and when.
    const formerTeachers = Array.isArray(participants)
      ? participants.filter((p) => p.role === PARTICIPANT_ROLE.TEACHER && p.status === 'left')
      : [];
    return [patientRecordId, {
      hasGuardian: !!guardianParticipant,
      guardianInviteStatus: guardianParticipant?.status || null,
      guardians,
      teachers,
      formerTeachers,
      schoolContact: schoolContact
        ? { firstName: schoolContact.firstName || '', lastName: schoolContact.lastName || '' }
        : null,
    }];
  } catch (_) {
    return [patientRecordId, { hasGuardian: false, guardianInviteStatus: null, guardians: [], teachers: [], formerTeachers: [], schoolContact: null }];
  }
}

/*
 * Data for the Harbour "Your pupils" page: the school user's referrals
 * enriched into pupil view models. The acquisition pattern mirrors
 * ServiceUserDashboard (ensureReferrals, one patientRecords read per pupil,
 * participants once, a live patient-record listener per pupil folding the
 * formProgress mirror — client formInstances reads are provider-only,
 * TDI-154) so both surfaces stay in parity while they coexist.
 */
// `forceTokenRefresh` (useAuth's, passed by the page that already holds it, so
// no second auth listener opens) is called once a claimless first visit has
// linked the account; see the referrals fetch below.
export default function useSencoPupils({ orgId, claimsOrgId, forceTokenRefresh = null }) {
  const { getCachedReferrals, getCachedReferralsMeta, ensureReferrals } = useRmsState();
  // Held in a ref so a new auth state never re-runs the referrals fetch.
  const forceTokenRefreshRef = useRef(forceTokenRefresh);
  forceTokenRefreshRef.current = forceTokenRefresh;
  // The organisation's HFP instrument setting (TDI-523): decides the form set
  // of every pupil whose profile has not started. Live, so the switch to v4
  // reaches an open board. Returned for the pages that list a pupil's forms.
  const orgHfpInstrument = useOrgHfpInstrument(orgId);
  const [rows, setRows] = useState(() => getCachedReferrals(orgId));
  const [patientsById, setPatientsById] = useState({});
  // Pupils whose record listener has delivered its first snapshot or failed.
  // Until every listed pupil is settled the hook still reports `loading`:
  // pages gate on record fields (consent, confirmations, progress), and a
  // record that has not arrived yet reads as empty, which sent parents to the
  // consent page and back home.
  const [settledIds, setSettledIds] = useState(() => new Set());
  const [participantsById, setParticipantsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [error, setError] = useState(null);

  // Bumped by reload(): people (teachers, guardian invites) change through
  // callables, not the record, so the listeners below never see them.
  const [participantsVersion, setParticipantsVersion] = useState(0);

  // `silent` refreshes without the loading state, for a page that is already
  // showing the pupil (after adding a teacher, or sending an invite).
  const reload = useCallback(async ({ force = true, silent = false } = {}) => {
    setParticipantsVersion((v) => v + 1);
    try {
      if (!silent) setLoading(true);
      setError(null);
      const fresh = await ensureReferrals({ orgId, force });
      setRows(fresh || []);
    } catch (e) {
      setError(e?.message || 'Could not load your pupils');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [ensureReferrals, orgId]);

  // Initial referrals fetch; force past the cache when claims are not set yet
  // (first visit via invite link, before lazy linking lands).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const cached = getCachedReferrals(orgId);
        if (!cancelled && cached && cached.length) setRows(cached);
        const fresh = await ensureReferrals({ orgId, force: !claimsOrgId });
        // A parent arriving straight from their invite link (TDI-676) has no
        // claims until that fetch links their account server-side. Refresh
        // the token before the record listeners start, because the rules only
        // let a token carrying the organisation read the record. The old
        // dashboard did this when families passed through it.
        if (!cancelled && !claimsOrgId && fresh?.length && forceTokenRefreshRef.current) {
          try { await forceTokenRefreshRef.current(); } catch (_) { /* the listeners soft-fail */ }
        }
        if (!cancelled) setRows(fresh || []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load your pupils');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, claimsOrgId, getCachedReferrals, ensureReferrals]);

  const patientIdsKey = useMemo(
    () => (rows || []).map((r) => r?.patientRecordId).filter(Boolean).join(','),
    [rows]
  );

  // Live patientRecords listeners: triage state, intake concern, child details
  // AND the formProgress mirror (TDI-154) all stream off the record while the
  // SENCo looks at the board — one listener per pupil, shared by every
  // consumer, so the board's two views of the same record can never disagree.
  // completionLoading clears once every pupil's first snapshot (or listener
  // error) has landed and the participants fetch has settled.
  useEffect(() => {
    if (!orgId || !patientIdsKey) return undefined;
    setCompletionLoading(true);
    const patientIds = patientIdsKey.split(',');
    const pendingFirstSnapshot = new Set(patientIds);
    let participantsSettled = false;
    const maybeFinishLoading = () => {
      if (participantsSettled && pendingFirstSnapshot.size === 0) setCompletionLoading(false);
    };
    const markDelivered = (patientRecordId) => {
      pendingFirstSnapshot.delete(patientRecordId);
      setSettledIds((prev) => (prev.has(patientRecordId) ? prev : new Set(prev).add(patientRecordId)));
      maybeFinishLoading();
    };

    const unsubscribes = patientIds.map((patientRecordId) =>
      onSnapshot(
        doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId),
        (snap) => {
          const data = snap.exists() ? snap.data() || {} : {};
          setPatientsById((prev) => ({ ...prev, [patientRecordId]: data }));
          markDelivered(patientRecordId);
        },
        () => {
          // Soft-fail: the card renders from the referral row alone, but the
          // board must still leave its loading state.
          markDelivered(patientRecordId);
        }
      )
    );

    // Participants once (dashboard pattern).
    Promise.all(patientIds.map((patientRecordId) => fetchParticipantData(orgId, patientRecordId))).then((entries) => {
      setParticipantsById((prev) => {
        const next = { ...prev };
        entries.forEach(([id, data]) => { next[id] = data; });
        return next;
      });
    }).finally(() => {
      participantsSettled = true;
      maybeFinishLoading();
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [orgId, patientIdsKey]);

  // Refetch the people after reload(); the first fetch rides with the
  // listeners above.
  useEffect(() => {
    if (!participantsVersion || !orgId || !patientIdsKey) return undefined;
    let cancelled = false;
    Promise.all(patientIdsKey.split(',').map((id) => fetchParticipantData(orgId, id))).then((entries) => {
      if (cancelled) return;
      setParticipantsById((prev) => {
        const next = { ...prev };
        entries.forEach(([id, data]) => { next[id] = data; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [participantsVersion, orgId, patientIdsKey]);

  // Per-pupil completion view, derived from the streamed records and the
  // one-shot participants fetch. Same shape the pages consumed before. A pupil
  // has no entry until their record's first snapshot lands (or none at all if
  // the listener errors), so statusUnknown keeps meaning "never heard from
  // Firestore", exactly as before.
  const completionData = useMemo(() => {
    const out = {};
    patientIdsKey.split(',').filter(Boolean).forEach((patientRecordId) => {
      if (!(patientRecordId in patientsById)) return;
      const { formProgress, triageAssigned } = buildFormProgressFromRecord(patientsById[patientRecordId]);
      const participantData = participantsById[patientRecordId] || {};
      out[patientRecordId] = {
        formProgress,
        triageAssigned,
        hasGuardian:
          participantData.hasGuardian ||
          Object.keys(formProgress).some((k) => k.endsWith('_guardian')),
        guardianInviteStatus: participantData.guardianInviteStatus || null,
        guardians: participantData.guardians || [],
        teachers: participantData.teachers || [],
        formerTeachers: participantData.formerTeachers || [],
        schoolContact: participantData.schoolContact || null,
      };
    });
    return out;
  }, [patientIdsKey, patientsById, participantsById]);

  const pupils = useMemo(() => {
    return (rows || [])
      .filter((r) => r?.patientRecordId)
      .map((row) => {
        const patientRecordId = row.patientRecordId;
        const patient = patientsById[patientRecordId] || {};
        const cd = completionData[patientRecordId] || {};
        const formProgress = cd.formProgress || {};
        const schoolPhase = patient.schoolPhase || patient.school?.schoolType || null;
        const intakeConcern = patient.intakeConcern || null;

        const first = patient.subject_first_name || patient.firstName || patient.subject?.firstName || '';
        const last = patient.subject_last_name || patient.lastName || patient.subject?.lastName || '';
        const name =
          [first, last].filter(Boolean).join(' ').trim() ||
          row.patientName ||
          (row.patient && (row.patient.fullName || [row.patient.firstName, row.patient.lastName].filter(Boolean).join(' '))) ||
          'Pupil';
        const dob = patient.subject_dob || patient.dob || patient.subject?.dob || null;

        // `patient` selects the HFP set per record (hfpFormKeysForRecord):
        // v3 for Harbour pupils, v2 for legacy referrals listed here too.
        const roleStatusFor = (role) =>
          calculateRoleStatus({
            role,
            formProgress,
            triageAssigned: cd.triageAssigned,
            hasGuardian: cd.hasGuardian,
            guardianInviteStatus: cd.guardianInviteStatus,
            schoolPhase,
            intakeConcern,
            patient,
          });
        const schoolStatus = roleStatusFor('school_user');
        const guardianStatus = roleStatusFor('guardian');

        const schoolDone = isRoleReferralFormsComplete('school_user', formProgress, intakeConcern);
        const guardianDone = isRoleReferralFormsComplete('guardian', formProgress, intakeConcern);
        const triageAssignedFlag = Boolean(patient.triageFormsAssigned) || Boolean(cd.triageAssigned);
        // Records gated on the confirm-and-submit step (TDI-418) are only
        // "sent" once both respondents have confirmed — completion alone no
        // longer submits the referral. Legacy records keep the old rule.
        const confirmationsIn =
          !recordRequiresSubmissionConfirmation(patient) || allRequiredRespondentsConfirmed(patient);
        const allFormsIn =
          schoolDone && guardianDone &&
          (!triageAssignedFlag || areAllTriageFormsComplete(formProgress)) &&
          confirmationsIn;
        const readyForTriage = patient?.triageReadiness?.status === 'ready';

        const pathways = buildReferralPathwayRows(patient, { readyForTriage, allFormsIn });
        const referralActive = pathways.length > 0
          || Boolean(patient.referralPathway && patient.referralPathway.startedAt);

        // SEN lane: HFP-forms-only statuses, so a SEN pupil reads honestly
        // without referral forms that were never asked.
        const senSchoolStatus = calculateSenRoleStatus({
          role: 'school_user',
          formProgress,
          hasGuardian: cd.hasGuardian,
          guardianInviteStatus: cd.guardianInviteStatus,
          patient,
          orgSetting: orgHfpInstrument,
        });
        const senGuardianStatus = calculateSenRoleStatus({
          role: 'guardian',
          formProgress,
          hasGuardian: cd.hasGuardian,
          guardianInviteStatus: cd.guardianInviteStatus,
          patient,
          orgSetting: orgHfpInstrument,
        });
        const sen = buildSenLane(patient, {
          schoolStatus: senSchoolStatus,
          guardianStatus: senGuardianStatus,
        });

        // Which teachers the school forms are waiting on (TDI-489, FR1): the
        // home rows name them while the school side is unfinished; the record
        // flyout's teachers box carries the per-form detail.
        // Each lane names only the teachers holding that lane's forms.
        const senActive = (patient.senPathway || {}).status === 'active';
        const withTeachers = (lane) => (row) => {
          const names = teacherNamesForLane(cd.teachers, lane, senActive);
          return names.length && row.key === 'school-forms' && row.chip?.status !== 'done'
            ? { ...row, label: `${row.label} · with ${names.join(', ')}` }
            : row;
        };
        if (sen && Array.isArray(sen.rows)) sen.rows = sen.rows.map(withTeachers('sen'));

        // The parent's finished forms are sent nowhere until they confirm
        // (TDI-598), so the SENCO's row must not read "Done" before then
        // (TDI-797): it shows the parent's own card's "Ready to submit", and
        // the SENCO knows to chase. The SEND rows already behave this way.
        const guardianAwaitingConfirm = recordRequiresSubmissionConfirmation(patient)
          && canConfirmSubmission({
            patient,
            formProgress,
            triageAssigned: cd.triageAssigned,
            role: 'guardian',
            pathway: 'referral',
          });
        const guardianReferralChip = guardianAwaitingConfirm
          ? { status: 'inProgress', label: 'Ready to submit' }
          : roleFormChip(guardianStatus);

        const referralFormRows = [
          withTeachers('referral')({ key: 'school-forms', label: 'School forms', chip: { ...roleFormChip(schoolStatus), tone: 'neutral' } }),
          { key: 'parent-forms', label: 'Parent or carer forms', chip: { ...guardianReferralChip, tone: 'neutral' } },
        ];
        const guardians = cd.guardians || [];

        return {
          id: patientRecordId,
          name,
          // The recorded year group wins, with the date of birth as the
          // fallback — the rule effectiveYearGroup and the server's
          // _record_year_group already use to judge pilot eligibility and
          // which phase's forms a pupil gets. Deriving it here instead meant
          // the screen could name one year group while the system acted on
          // another: on staging four of five pupils displayed a year above
          // the one their record held.
          yearGroup: patient.yearGroup || deriveYearGroup(dob),
          dob,
          createdAt: row.createdAt || null,
          sen,
          senOnly: isSenOnlyRecord(patient),
          referral: referralActive
            ? {
                pathways,
                // One line per pathway once the referral is sent (TDI-694).
                rows: referralLaneRows(pathways, referralFormRows),
                formRows: referralFormRows,
              }
            : null,
          schoolStatus,
          guardianStatus,
          senSchoolStatus,
          senGuardianStatus,
          formProgress,
          guardians,
          guardian: guardians.find((g) => g.status === 'account_linked') || guardians[0] || null,
          teachers: cd.teachers || [],
          formerTeachers: cd.formerTeachers || [],
          schoolContact: cd.schoolContact || null,
          informantParticipantId: patient.informantParticipantId || null,
          patient,
          statusUnknown: !completionData[patientRecordId],
          // Whether Firestore has answered for this record at all (first
          // snapshot or listener error). statusUnknown with recordSettled
          // means the listener failed, so a screen can say so rather than
          // show a half-built pupil or load for ever (TDI-807).
          recordSettled: settledIds.has(patientRecordId),
        };
      });
  }, [rows, patientsById, completionData, orgHfpInstrument, settledIds]);

  // Only the first load waits for the records: a pupil added later (after the
  // SENCo adds one) must not flip a mounted page back to its loading state.
  const [initialRecordsSettled, setInitialRecordsSettled] = useState(false);
  const recordsPending = !initialRecordsSettled &&
    patientIdsKey.split(',').some((id) => id && !settledIds.has(id));
  useEffect(() => {
    if (!initialRecordsSettled && !loading && !recordsPending) setInitialRecordsSettled(true);
  }, [initialRecordsSettled, loading, recordsPending]);

  return {
    pupils,
    loading: loading || recordsPending,
    completionLoading,
    error,
    reload,
    hasAny: (rows || []).length > 0,
    // The signed-in email reaches no record because its invitation was
    // retired, e.g. the school changed the parent's email (TDI-803).
    invitationRetired: (rows || []).length === 0
      && ((getCachedReferralsMeta ? getCachedReferralsMeta(orgId) : {}).invitationRetired === true),
    orgHfpInstrument,
  };
}

export { STATUS };
